import React, { useEffect, useState } from 'react';
import * as FaIcons from 'react-icons/fa6';
import { FaTelegram } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import './link.css';


const FaTelegramIcon = FaTelegram as React.ComponentType<{ className?: string }>;
const FaWindows = FaIcons.FaWindows as React.ComponentType<{ className?: string }>;
const FaApple = FaIcons.FaApple as React.ComponentType<{ className?: string }>;
const FaLinux = FaIcons.FaLinux as React.ComponentType<{ className?: string }>;
const FaAndroid = FaIcons.FaAndroid as React.ComponentType<{ className?: string }>;
const FaMobile = FaIcons.FaMobile as React.ComponentType<{ className?: string }>;
const FaDownload = FaIcons.FaDownload as React.ComponentType<{ className?: string }>;
const FaUsers = FaIcons.FaUsers as React.ComponentType<{ className?: string }>;
const FaTriangleExclamation = FaIcons.FaTriangleExclamation as React.ComponentType<{ className?: string }>;

interface LocationState {
  fromForm?: boolean;
  name?: string;
}

const TelegramLink: React.FC = () => {
  interface DeviceInfo {
    isMobile: boolean;
    os: 'unknown' | 'windows' | 'macos' | 'linux' | 'android' | 'ios';
    hasTelegram: boolean;
    isWebBrowser: boolean;
  }

  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isMobile: false,
    os: 'unknown',
    hasTelegram: false,
    isWebBrowser: false
  });
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const [isCheckingName, setIsCheckingName] = useState<boolean>(true);

  const detectDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    let isMobile = false;
    let os: 'unknown' | 'windows' | 'macos' | 'linux' | 'android' | 'ios' = 'unknown';
    let isWebBrowser = false;

    if (window.location.hostname.includes('web.telegram.org') ||
      /WebApp/i.test(userAgent)) {
      isWebBrowser = true;
    }

    if (/windows/i.test(userAgent)) {
      os = 'windows';
    } else if (/macintosh|mac os x/i.test(userAgent)) {
      os = 'macos';
    } else if (/linux/i.test(userAgent)) {
      os = 'linux';
    } else if (/android/i.test(userAgent)) {
      isMobile = true;
      os = 'android';
    } else if (/iphone|ipad|ipod/i.test(userAgent)) {
      isMobile = true;
      os = 'ios';
    }

    setDeviceInfo({ isMobile, os, hasTelegram: false, isWebBrowser });
  };

  const handleButtonClick = (url: string) => {
    window.location.href = url;
  };

  const renderDeviceMessage = () => {
    if (deviceInfo.isWebBrowser) {
      return (
        <div className="web-warning">
          <FaTriangleExclamation className="warning-icon" />
          <span>Você está acessando pelo navegador web. Por favor, acesse pelo aplicativo em seu dispositivo móvel ou pelo Telegram Desktop.</span>
        </div>
      );
    }

    if (deviceInfo.isMobile) {
      if (!deviceInfo.hasTelegram) {
        return (
          <div className="device-hint">
            {deviceInfo.os === 'ios' ? (
              <>
                <FaApple className="os-icon" />
                <span>Instale o Telegram na <strong>App Store</strong> para continuar</span>
              </>
            ) : (
              <>
                <FaAndroid className="os-icon" />
                <span>Instale o Telegram na <strong>Google Play Store</strong> para continuar</span>
              </>
            )}
          </div>
        );
      }
      return null;
    } else {
      let icon = null;
      let text = '';

      switch (deviceInfo.os) {
        case 'windows':
          icon = <FaWindows className="os-icon" />;
          text = 'Abra no <strong>Windows</strong> com o Telegram';
          break;
        case 'macos':
          icon = <FaApple className="os-icon" />;
          text = 'Abra na <strong>Apple</strong> com o Telegram';
          break;
        case 'linux':
          icon = <FaLinux className="os-icon" />;
          text = 'Abra no <strong>Linux</strong> com o Telegram';
          break;
        default:
          icon = <FaMobile className="os-icon" />;
          text = 'Abra no <strong>celular</strong> para melhor experiência';
      }

      return (
        <div className="device-hint">
          {icon}
          <span dangerouslySetInnerHTML={{ __html: text }} />
        </div>
      );
    }
  };

  useEffect(() => {
    const state = location.state;
    if (!state?.fromForm || !state?.name) {
      Swal.fire({
        icon: 'warning',
        title: 'Opa!',
        text: 'Acessou sem nome? Pois é! Você precisa preencher seu nome no formulário antes de acessar esta página.',
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/', { replace: true });
        }
      });
      return;
    }
    setIsCheckingName(false);
    detectDevice();
  }, [location.state, navigate]);

  if (isCheckingName || !state?.name) {
    return null;
  }

  if (deviceInfo.isWebBrowser) {
    return (
      <div className="telegram-container web-blocked">
        <div className="telegram-content">
          <div className="telegram-card warning-card">
            <div className="card-icon warning">
              <FaTriangleExclamation className="users-icon" />
            </div>

            <h1 className="card-title">Acesso não permitido</h1>

            <p className="card-description">
              Este conteúdo só está disponível no aplicativo nativo do Telegram.
              <br />
              Por favor, acesse pelo aplicativo em seu dispositivo móvel ou pelo Telegram Desktop.
            </p>

            <div className="action-buttons">
              <button
                onClick={() => handleButtonClick('https://telegram.org/dl')}
                className="primary-button"
              >
                <FaDownload className="download-icon" />
                Baixar Telegram
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="telegram-container">
      <div className="background-animation">
        <div className="gradient-circle circle-1"></div>
        <div className="gradient-circle circle-2"></div>
        <div className="gradient-circle circle-3"></div>
      </div>

      <div className="telegram-content">
        <header className="telegram-header">
          <div className="logo-container">
            <FaTelegramIcon className="logo-icon" />
            <span className="logo-text">Telegram</span>
          </div>
          <button
            onClick={() => handleButtonClick('https://telegram.org/dl')}
            className="download-button"
          >
            <FaDownload className="download-icon" />
            <span>Download</span>
          </button>
        </header>

        <main className="telegram-main">
          <div className="telegram-card">
            <div className="card-icon">
              <FaUsers className="users-icon" />
            </div>

            {state?.name && <p className="user-greeting">Olá, {state.name}!</p>}

            <div className="card-description">
              <p>Você pode entrar no nosso grupo diretamente no Telegram ou baixar o aplicativo Telegram se ainda não tiver instalado.</p>
              {renderDeviceMessage()}
            </div>

            <div className="action-buttons">
              {deviceInfo.hasTelegram || !deviceInfo.isMobile ? (
                <button
                  onClick={() => handleButtonClick("tg://addlist?slug=-TDiAYpp3gs0NTBh")}
                  className="primary-button"
                >
                  {deviceInfo.isMobile ? 'Abrir no Telegram' : 'Abrir no Telegram'}
                </button>
              ) : null}

              {(!deviceInfo.hasTelegram && deviceInfo.isMobile) && (
                <button
                  onClick={() => handleButtonClick('https://telegram.org/dl')}
                  className="secondary-button"
                >
                  Baixar Telegram para {deviceInfo.os === 'ios' ? 'iOS' : 'Android'}
                </button>
              )}
            </div>
          </div>
        </main>

        <footer className="telegram-footer">
          <p>{new Date().getFullYear()}&nbsp;&copy;&nbsp;Todos os direitos reservados.</p>
          <p>Este site não é afiliado ao <b>Telegram</b>.</p>
        </footer>
      </div>
    </div>
  );
};
export default TelegramLink;
